import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  CheckIcon,
  SelectorIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/solid";
import { InformationCircleIcon, SearchIcon } from "@heroicons/react/outline";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import { classNames, formatCurrency, getErrorMessages } from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { useLocation, useNavigate } from "react-router-dom";
import {
  requestAddMainBusiness,
  requestListBusiness,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../Context";
import PeriodLockGate from "../../Components/v2/Accounting/PeriodLockGate";
import {
  useAccountingDispatch,
  useAccountingState,
} from "../../Context/accounting/context";
import {
  clearAccountingAccount,
  clearAcctAccountError,
  clearInitAccountStatus,
  clearUpdateOpeningBalance,
  requestInitAccount,
  requestListAccount,
  requestUpdateOpeningBalance,
} from "../../Context/accounting/actions";
import TableSkeleton from "../../Components/TableSkeleton";
import EmptyState from "../../Components/EmptyState";
import { EmptyBusinessSkeleton } from "../../Components/EmptyBusinessSkeleton";
import { Alert } from "../../Components/Alert";
import ReactDatePicker from "react-datepicker";
import { CalendarIcon } from "@heroicons/react/outline";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { ModalAlert } from "../../Components/ModalAlert";
import EmptyBusiness from "../../Components/EmptyBusiness";
import { ACCOUNT_CATEGORY_OPTIONS } from "../../Constants/accounting";
import {
  RETAINED_EARNINGS_CODE,
  isProfitLossAccount,
  sortByCategoryOrder,
} from "../../Utils/balanceSheet";
import { isBusinessMainBlocked } from "../../Constants/business";
import { parseJsonObject } from "../../Utils";
import {
  getLastFiscalYearEnd,
  wibEndOfDay,
  wibStartOfDay,
} from "../../Utils/PeriodUtils";

// The figures are parked here while the user is away creating account 330999.
const OB_FORM_STATE_KEY = "ob_form_state";

export const OpeningBalance = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const initialLoad = useRef(false);

  const [selectedBusiness, setselectedBusiness] = useState({});
  const [debitValues, setDebitValues] = useState({});
  const [creditValues, setCreditValues] = useState({});
  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [date, setDate] = useState(() => getLastFiscalYearEnd(null));
  const restoredDateRef = useRef(false);
  const [submitted, setsubmitted] = useState(false);

  // Restore the figures after a detour to create account 330999. Typing a whole
  // opening balance twice is not a reasonable price for a missing account.
  useEffect(() => {
    const saved = sessionStorage.getItem(OB_FORM_STATE_KEY);
    if (!saved) return;
    sessionStorage.removeItem(OB_FORM_STATE_KEY);

    const {
      debitValues: savedDebit,
      creditValues: savedCredit,
      date: savedDate,
    } = parseJsonObject(saved);
    if (savedDebit) setDebitValues(savedDebit);
    if (savedCredit) setCreditValues(savedCredit);
    if (savedDate) {
      const restored = new Date(savedDate);
      if (!Number.isNaN(restored.getTime())) {
        restoredDateRef.current = true;
        setDate(restored);
      }
    }
  }, []);

  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [showErrorModal, setshowErrorModal] = useState(false);
  const [showConfirmModal, setshowConfirmModal] = useState(false);
  const [showCreateAccountModal, setshowCreateAccountModal] = useState(false);

  const [businessError, setbusinessError] = useState(false);
  const [dateError, setdateError] = useState(false);

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const authDispatch = useAuthDispatch();

  // An opening balance is dated at most the end of the business's last fiscal
  // year (31 December for a January year, 30 June for a July one), so once the
  // books are closed through that day there is no acceptable date left. Gating
  // the whole form rather than constraining the picker: a calendar where every
  // day is disabled explains nothing, and a form that must fail is not honest.
  const { isDateClosed, closedThroughLabel } = usePeriodLock(
    selectedBusiness?.id,
    { locale: i18n.language },
  );
  const fiscalYearStartMonth = selectedBusiness?.fiscalYearStartMonth;
  const openingDate = useMemo(
    () => getLastFiscalYearEnd(fiscalYearStartMonth),
    [fiscalYearStartMonth],
  );
  const periodLocked = isDateClosed(wibStartOfDay(openingDate));

  // Follow the selected business's fiscal year once it is known or switched,
  // without undoing a date restored from the detour above.
  const openingDateTime = openingDate.getTime();
  const lastOpeningDateRef = useRef(openingDateTime);
  useEffect(() => {
    if (lastOpeningDateRef.current === openingDateTime) return;
    lastOpeningDateRef.current = openingDateTime;
    // A date the user picked before the detour to create 330999 stands.
    if (restoredDateRef.current) {
      restoredDateRef.current = false;
      return;
    }
    setDate(new Date(openingDateTime));
  }, [openingDateTime]);

  const { businesses, loading } = useBusinessTransactionState();
  const dispatch = useAccountingDispatch();
  const {
    accounts,
    accountError,
    accountLoading,
    accountID,
    initAccountLoading,
    initAccountStatus,
    transactionID,
    submitLoading,
    submitError,
  } = useAccountingState();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  const { onboarding } = location.state || {};

  const businessExists = useMemo(() => {
    return businesses.some((business) => business.id === businessIdParam);
  }, [businessIdParam, businesses]);

  const totalDebit = useMemo(() => {
    const debits = Object.values(debitValues).map(parseFloat);
    return debits.reduce((acc, val) => acc + val, 0);
  }, [debitValues]);

  const totalCredit = useMemo(() => {
    const credits = Object.values(creditValues).map(parseFloat);
    return credits.reduce((acc, val) => acc + val, 0);
  }, [creditValues]);

  // Income statement accounts never carry an opening balance.
  const balanceAccounts = useMemo(
    () => accounts.filter((account) => !isProfitLossAccount(account)),
    [accounts]
  );

  const retainedEarningsAccount = useMemo(
    () => accounts.find((account) => account.code === RETAINED_EARNINGS_CODE),
    [accounts]
  );

  const changedAccounts = useMemo(() => {
    return balanceAccounts
      .map((account) => ({
        id: account.id,
        debit: debitValues[account.id] || 0,
        credit: creditValues[account.id] || 0,
      }))
      .filter((account) => account.debit !== 0 || account.credit !== 0);
  }, [balanceAccounts, debitValues, creditValues]);

  const validTotal = useMemo(() => {
    return totalDebit === totalCredit;
  }, [totalDebit, totalCredit]);

  const openingBalanceAccount = useMemo(() => {
    return accounts.find((account) => account.code === "330999");
  }, [accounts]);

  const difference = formatCurrency(totalDebit - totalCredit);

  // Helper function to get category label
  const getCategoryLabel = (categoryValue) => {
    const category = ACCOUNT_CATEGORY_OPTIONS.find(
      (cat) => cat.value === categoryValue
    );
    return category ? category.label : categoryValue;
  };

  // Filter accounts based on search query
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return balanceAccounts;

    const query = searchQuery.toLowerCase();
    return balanceAccounts.filter(
      (account) =>
        account.name?.toLowerCase().includes(query) ||
        account.code?.toLowerCase().includes(query)
    );
  }, [balanceAccounts, searchQuery]);

  // Group accounts by category, in chart of accounts order, so a liability
  // moved to long-term lists after Other Current Liabilities.
  const groupedAccounts = useMemo(() => {
    const groups = {};
    sortByCategoryOrder(filteredAccounts).forEach((account) => {
      const category = account.category || "Other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(account);
    });
    return groups;
  }, [filteredAccounts]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam
          );
          if (business) {
            setselectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setselectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setselectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setselectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedBusiness.value, onboarding]);

  useEffect(() => {
    if (accountID) {
      clearAccountingAccount(dispatch);
    }
    clearAcctAccountError(dispatch);
    clearUpdateOpeningBalance(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(dispatch, {
        businessID: selectedBusiness.id,
      });
      navigate(`/account?id=${selectedBusiness?.id}`);
      clearInitAccountStatus(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  useEffect(() => {
    const initialDebitValues = {};
    const initialCreditValues = {};
    accounts.forEach((account) => {
      initialDebitValues[account.id] = 0;
      initialCreditValues[account.id] = 0;
    });
    setDebitValues(initialDebitValues);
    setCreditValues(initialCreditValues);
  }, [accounts]);

  useEffect(() => {
    if (submitted) {
      const isBusinessValid = Boolean(selectedBusiness.value);
      const isDateValid = Boolean(date);

      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Business is required.");
      }

      if (isDateValid) {
        setErrorMessage("date", "");
      } else {
        setErrorMessage("date", "Date is required.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness, date, submitted]);

  useEffect(() => {
    if (!transactionID) {
      return;
    }
    setshowConfirmModal(false);
    setshowSuccessModal(true);
  }, [transactionID]);

  useEffect(() => {
    if (submitError) {
      setshowConfirmModal(false);
      setshowErrorModal(true);
    }
  }, [submitError]);

  const onLoad = () => {
    if (selectedBusiness.value) {
      requestListAccount(dispatch, {
        page: 1,
        loadAll: true,
        loadAllLoading: true,
        businessID: selectedBusiness.value,
      });
    }
  };

  const handleInitAccount = () => {
    requestInitAccount(dispatch, {
      businessID: selectedBusiness?.id,
    });
  };

  const handleDebitChange = (id, value) => {
    setDebitValues((prev) => ({
      ...prev,
      [id]: parseFloat(value),
    }));
  };

  const handleCreditChange = (id, value) => {
    setCreditValues((prev) => ({
      ...prev,
      [id]: parseFloat(value),
    }));
  };

  const handleCellClick = (id, field) => {
    setEditingCell({ id, field });
  };

  const handleBlur = () => {
    setEditingCell({ id: null, field: null });
  };

  const validateInput = () => {
    let currentValidBusiness = true;
    let currentValidDate = true;
    let currentValidAccount = true;

    currentValidBusiness = Boolean(selectedBusiness.value);
    currentValidDate = Boolean(date);
    currentValidAccount = changedAccounts.length > 0;

    return currentValidBusiness && currentValidDate && currentValidAccount;
  };

  const onSubmit = (force = false) => {
    if (periodLocked) return;
    setsubmitted(true);
    const valid = validateInput();
    if (valid && (validTotal || force)) {
      const input = {
        business_id: selectedBusiness.id,
        // The close of that day in WIB, not the laptop's midnight.
        created_at: wibEndOfDay(date),
        accounts: changedAccounts,
      };
      requestUpdateOpeningBalance(dispatch, { input });
    } else if (dateError || businessError) {
      setshowConfirmModal(false);
    } else if (!validTotal && !openingBalanceAccount) {
      // The server parks an unbalanced difference in capital account 330999.
      // Without that account the save is refused, so offer to create it instead
      // of repeating "the totals must match" at someone whose totals never will.
      setshowCreateAccountModal(true);
    } else {
      if (openingBalanceAccount) {
        setshowConfirmModal(true);
      } else {
        setshowErrorModal(true);
      }
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "business":
        setbusinessError(message);
        break;
      case "date":
        setdateError(message);
        break;
      default:
        break;
    }
  };

  const navigateToAccount = () => {
    clearUpdateOpeningBalance(dispatch);
    setshowSuccessModal(false);
    if (onboarding) {
      navigate("/v2/accounting");
    } else {
      navigate("/account");
    }
  };

  const EmptyAccount = () => {
    return initAccountLoading ? (
      <EmptyBusinessSkeleton />
    ) : accountError ? (
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-t-lg">
        <Alert
          title="Error"
          type="error"
          message={accountError?.message || "Error fetch accounting account."}
          errors={accountError?.errors}
        />
      </div>
    ) : (
      <EmptyState
        titleKey="No Accounts"
        descriptionKey="Please create an account first"
      >
        <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6">
          <Button
            outline
            className="flex-1"
            onClick={handleInitAccount}
            title="Create Automatically"
          />

          <Button
            outline
            className="flex-1"
            onClick={() =>
              navigate(`/account/import?id=${selectedBusiness.id}`)
            }
            title="Import Using XLSX File"
          />
        </div>
      </EmptyState>
    );
  };

  if (businesses.length === 0) {
    return <EmptyBusiness loading={loading} />;
  }

  if (businessIdParam && !businessExists) {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert type="error" title="Error" message="Business not found." />
        </div>
      </div>
    );
  }

  if (accounts.length === 0 && !initialLoad.current) {
    return accountLoading ? <EmptyBusinessSkeleton /> : <EmptyAccount />;
  }

  return (
    <div className="space-y-4">
      {transactionID && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description={"Your opening balance has been updated."}
          type="SUCCESS"
          onConfirm={navigateToAccount}
        />
      )}
      {(submitError || !openingBalanceAccount) && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowErrorModal(open);
          }}
          title="Error"
          description={
            submitError
              ? getErrorMessages(submitError)
              : "Total debit and credit must be the same."
          }
          onConfirm={() => {
            setshowErrorModal(false);
            clearUpdateOpeningBalance(dispatch);
          }}
        />
      )}
      {!validTotal && !openingBalanceAccount && (
        <ModalAlert
          open={showCreateAccountModal}
          setOpen={(open) => {
            setshowCreateAccountModal(open);
          }}
          title="accounts.modal.title.add"
          // Interpolated here: ModalAlert translates its props, but without
          // passing options through, so `difference` has to be resolved first.
          description={t("debitCreditCreateAccountConfirm", { difference })}
          confirmButtonText="common.create"
          cancelButtonText="common.cancel"
          onConfirm={() => {
            setshowCreateAccountModal(false);
            sessionStorage.setItem(
              OB_FORM_STATE_KEY,
              JSON.stringify({
                debitValues,
                creditValues,
                date: date?.toISOString(),
              }),
            );
            const returnTo = encodeURIComponent(
              `/opening-balance?id=${selectedBusiness.id}`,
            );
            navigate(
              `/account/create?id=${selectedBusiness.id}&name=Saldo+Awal&code=330999&category=CAPITAL&returnTo=${returnTo}`,
            );
          }}
          type="warning"
        />
      )}
      {!validTotal && (
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setshowConfirmModal(open);
          }}
          title="Simpan Perubahan Ini"
          description={t("debitCreditUnbalanceWarning", {
            difference,
            account: openingBalanceAccount?.name,
          })}
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          onConfirm={() => onSubmit(true)}
          loadingButton={submitLoading}
          disabledButton={submitLoading}
          type="warning"
        />
      )}
      <div className="flex flex-col flex-wrap sm:flex-row gap-4">
        <div>
          <Listbox
            value={selectedBusiness}
            onChange={(value) => {
              initialLoad.current = false;
              setselectedBusiness(value);
            }}
          >
            {({ open }) => (
              <>
                <ListboxButton
                  className={classNames(
                    businessError ? "border-red-500" : "border-gray-300",
                    "flex items-center justify-between w-full sm:w-48 py-2 px-3 border rounded-md bg-white shadow-sm text-sm"
                  )}
                >
                  <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                    {selectedBusiness?.label || t("Business Name")}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-auto rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                    {businesses.map((business) => (
                      <ListboxOption
                        key={business.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9"
                          )
                        }
                        value={business}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate"
                                )}
                              >
                                {business.label}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4"
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        {/* Hidden with the rest of the form when locked — the business picker
            above stays, so the user can switch to a business that is still
            open instead of hitting a dead end. */}
        {!periodLocked && (
        <div className="relative flex">
          <ReactDatePicker
            className={classNames(
              dateError
                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
              "block pl-6 w-full text-sm rounded-md shadow-sm border-gray-300 cursor-pointer"
            )}
            selected={date}
            onChange={setDate}
            maxDate={openingDate}
            placeholderText={t("Select Date")}
            dateFormat="dd/MM/yyyy"
          />
          <CalendarIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-black" />
        </div>
        )}
      </div>
      <PeriodLockGate
        locked={periodLocked}
        closedThroughLabel={closedThroughLabel}
      >
      <div
        role="note"
        className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3"
      >
        <InformationCircleIcon
          className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <p className="text-sm text-blue-700">
          {t("openingBalances.balanceSheetOnly")}
          {retainedEarningsAccount && (
            <>
              {" "}
              {t("openingBalances.retainedEarnings")}{" "}
              <strong className="font-semibold">
                {`${retainedEarningsAccount.code} ${retainedEarningsAccount.name}`}
              </strong>
              .
            </>
          )}
        </p>
      </div>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value.trimStart())}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
          placeholder={t("searchByAccountNameOrCode")}
        />
      </div>
      {searchQuery.trim() && (
        <div className="text-sm text-gray-600">
          {filteredAccounts.length}{" "}
          {filteredAccounts.length === 1
            ? t("accountFound")
            : t("accountsFound")}
        </div>
      )}
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-auto max-h-[80vh] border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Code")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Account")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                      {t("Debit")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                      {t("Credit")}
                    </th>
                  </tr>
                </thead>
                {accountLoading ? (
                  <TableSkeleton rows={10} columns={5} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {accounts.length > 0 ? (
                      Object.keys(groupedAccounts).map((category) => (
                        <Disclosure
                          as={React.Fragment}
                          key={category}
                          defaultOpen={true}
                        >
                          {({ open }) => (
                            <>
                              <tr className="bg-gray-100 hover:bg-gray-200">
                                <td colSpan={1} className="px-4 py-3">
                                  <DisclosureButton className="flex items-center w-full text-left">
                                    <div className="flex items-center">
                                      {open ? (
                                        <ChevronDownIcon className="w-5 h-5 mr-2 text-gray-600" />
                                      ) : (
                                        <ChevronRightIcon className="w-5 h-5 mr-2 text-gray-600" />
                                      )}
                                      <span className="font-semibold text-sm text-gray-900">
                                        {t(getCategoryLabel(category))}
                                      </span>
                                    </div>
                                  </DisclosureButton>
                                </td>
                                <td colSpan={3} className="px-4 py-3">
                                  <span className="font-semibold text-sm text-gray-900">
                                    {groupedAccounts[category].length}{" "}
                                    {groupedAccounts[category].length === 1
                                      ? t("Account")
                                      : t("Accounts")}
                                  </span>
                                </td>
                              </tr>
                              <DisclosurePanel as={React.Fragment}>
                                <>
                                  {groupedAccounts[category].map((account) => (
                                    <tr
                                      key={account.id}
                                      className="hover:bg-gray-50"
                                    >
                                      <td className="px-4 py-2 font-medium text-sm text-gray-600 whitespace-nowrap pl-12">
                                        {account.code}
                                      </td>
                                      <td className="px-4 py-2 font-medium text-sm text-gray-600 whitespace-nowrap">
                                        {account.name}
                                      </td>
                                      <td
                                        className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap w-80 h-14 cursor-pointer"
                                        onClick={() =>
                                          handleCellClick(account.id, "debit")
                                        }
                                      >
                                        {editingCell.id === account.id &&
                                        editingCell.field === "debit" ? (
                                          <NumericFormat
                                            type="text"
                                            name="debit"
                                            id="debit"
                                            value={debitValues[account.id] || 0}
                                            thousandSeparator="."
                                            decimalSeparator=","
                                            displayType="input"
                                            onValueChange={(values) => {
                                              handleDebitChange(
                                                account.id,
                                                values.value
                                              );
                                            }}
                                            className="border px-2 py-1 w-full text-right"
                                            onBlur={handleBlur}
                                            autoFocus
                                          />
                                        ) : (
                                          <div className="p-2 hover:border">
                                            <NumericFormat
                                              displayType={"text"}
                                              value={
                                                debitValues[account.id] || 0
                                              }
                                              thousandSeparator={"."}
                                              decimalSeparator={","}
                                            />
                                          </div>
                                        )}
                                      </td>
                                      <td
                                        className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap w-80 h-14 cursor-pointer"
                                        onClick={() =>
                                          handleCellClick(account.id, "credit")
                                        }
                                      >
                                        {editingCell.id === account.id &&
                                        editingCell.field === "credit" ? (
                                          <NumericFormat
                                            type="text"
                                            name="credit"
                                            id="credit"
                                            value={
                                              creditValues[account.id] || 0
                                            }
                                            thousandSeparator="."
                                            decimalSeparator=","
                                            displayType="input"
                                            onValueChange={(values) => {
                                              handleCreditChange(
                                                account.id,
                                                values.value
                                              );
                                            }}
                                            className="border px-2 py-1 w-full text-right"
                                            onBlur={handleBlur}
                                            autoFocus
                                          />
                                        ) : (
                                          <div className="p-2 hover:border">
                                            <NumericFormat
                                              displayType={"text"}
                                              value={
                                                creditValues[account.id] || 0
                                              }
                                              thousandSeparator={"."}
                                              decimalSeparator={","}
                                            />
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              </DisclosurePanel>
                            </>
                          )}
                        </Disclosure>
                      ))
                    ) : (
                      <tr>
                        <td className="p-4" colSpan={4}>
                          {accountError ? (
                            <ErrorList
                              message={
                                accountError?.message ||
                                "Error fetching accounts."
                              }
                              errors={accountError?.errors}
                            />
                          ) : (
                            <EmptyList title="No Accounts" />
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
                <tfoot className="bg-gray-50 sticky bottom-0">
                  <tr>
                    <td
                      className="px-4 py-2 font-medium text-sm text-gray-700"
                      colSpan={2}
                    >
                      Total
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      <div className="p-2">
                        <NumericFormat
                          displayType={"text"}
                          value={totalDebit || 0}
                          thousandSeparator={"."}
                          decimalSeparator={","}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      <div className="p-2">
                        <NumericFormat
                          displayType={"text"}
                          value={totalCredit || 0}
                          thousandSeparator={"."}
                          decimalSeparator={","}
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4}>
                      <div className="bg-white flex justify-between p-4">
                        <div className="text-sm my-auto">
                          <p>
                            {`${t("Opening Balance Settings Per")} ${dayjs(date)
                              .locale(i18n.language)
                              .format("DD MMMM YYYY")}`}
                          </p>
                        </div>
                        <div className="flex gap-4">
                          {onboarding && (
                            <Button
                              outline
                              title={t("skip")}
                              onClick={() => navigate("/v2/accounting")}
                            />
                          )}
                          <Button
                            title={t("Save Changes")}
                            onClick={() => onSubmit(false)}
                            loading={submitLoading}
                            disabled={
                              submitLoading ||
                              periodLocked ||
                              changedAccounts.length === 0
                            }
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
      </PeriodLockGate>
    </div>
  );
};
